import {
  CheckoutSection,
  CheckoutSectionDisplayStage,
  AsyncCheckoutOrderType,
  UserInteractionType,
  UserRetailerCheckoutBatch,
  UserRetailerCheckoutBatchStatus,
  UserRetailerCheckoutEvent,
  UserRetailerCheckoutOrder,
  UserRetailerCheckoutOrderStatusV2,
  UserRetailerCheckoutSessionStatus,
} from "@/components/Checkout/types";
import {
  getAuthorizationHeader,
  getVisitorIdCookie,
  ProductRewardSearchResult,
  tryGetVisitorId,
  ShoppingCartItem,
  UserRetailerShoppingCart,
  CartItem,
} from "@brandclub/common-ui";
import { axiosRetailerSync } from "../../utils/axios";

import { UserRetailerCheckoutCommunicationResponse } from "@/components/Checkout/CheckoutMachine/types/FastCheckoutMachine";
import { CheckoutIntent } from "@/components/Checkout/components/CheckoutV2/types/CheckoutIntent";
import { encryptData } from "@/utils/encryptionUtils";
import { RetailerSyncSession, SyncStatus } from "../../types/misc";
import { getTOtp } from "./authenticated/ServerSideSync";

export interface IProducts {
  [stacklineSku: string]: ProductRewardSearchResult & {
    retailerOrderId?: string;
    retailerOrderPageUrl?: string;
  };
}

async function getHeadersWithUniqueVisitorId() {
  const uniqueVisitorId = await tryGetVisitorId();
  const uniqueVisitorIdFromCookie = getVisitorIdCookie();

  const uniqueVisitorIdToUse =
    uniqueVisitorIdFromCookie && uniqueVisitorIdFromCookie !== ""
      ? uniqueVisitorIdFromCookie
      : uniqueVisitorId;
  return {
    ...(await getAuthorizationHeader()),
    "x-bc-visitor-id": uniqueVisitorIdToUse,
  };
}

async function getHeaders() {
  return await getHeadersWithUniqueVisitorId();
}

export const getAllUserRetailerShoppingCarts = async (signal?: AbortSignal) => {
  const { data } = await axiosRetailerSync.get(
    `/retailerCheckout/getAllUserRetailerShoppingCarts`,
    {
      headers: await getHeadersWithUniqueVisitorId(),
      signal,
    }
  );

  return data as {
    cartItems: CartItem[];
    userRetailerShoppingCarts: UserRetailerShoppingCart[];
    products: IProducts;
  };
};

export const resetUserRetailerCurrentShoppingCart = async (
  {
    retailerId,
  }: {
    retailerId: number;
  },
  uniqueVisitorId?: string
) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/resetUserRetailerCurrentShoppingCart?retailerId=${retailerId}`,
    null,
    {
      headers: await getHeadersWithUniqueVisitorId(),
    }
  );
  return data as { userRetailerShoppingCart: UserRetailerShoppingCart };
};

const mapShoppingCartItemsToRequest = (
  items: ShoppingCartItem[]
): {
  brandId: number;
  stacklineSku: string;
  quantity: number;
  retailPrice: number;
  retailerSku: string;
  retailerId: number;
}[] => {
  return items.map((item) => ({
    brandId: item.brandId,
    stacklineSku: item.stacklineSku,
    quantity: item.quantity,
    retailPrice: item.retailPrice,
    retailerSku: item.retailerSku,
    retailerId: item.retailerId,
  }));
};

export const updateItemsToUserRetailerShoppingCart = async (
  {
    retailerId,
    items,
  }: {
    retailerId: number;
    items: ShoppingCartItem[];
  },
  uniqueVisitorId?: string,
  signal?: AbortSignal
) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/updateItemsToUserRetailerShoppingCart`,
    { retailerId, items: mapShoppingCartItemsToRequest(items) },
    {
      headers: await getHeadersWithUniqueVisitorId(),
      signal,
    }
  );
  return data as { userRetailerShoppingCart: UserRetailerShoppingCart };
};

export const removeItemFromShoppingCart = async (
  {
    retailerId,
    items,
  }: {
    retailerId: number;
    items: ShoppingCartItem[];
  },
  signal?: AbortSignal
) => {
  const request = {
    retailerId,
    items: mapShoppingCartItemsToRequest(items),
  };

  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/removeItemsToUserRetailerShoppingCart`,
    request,
    {
      headers: await getHeadersWithUniqueVisitorId(),
      signal,
    }
  );
  return data as { userRetailerShoppingCart: UserRetailerShoppingCart };
};

export const moveAllUserRetailerShoppingCarts = async (
  uniqueVisitorId: string
) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/moveAllUserRetailerShoppingCarts`,
    null,
    {
      headers: await getHeadersWithUniqueVisitorId(),
    }
  );
  return data;
};

export const initiateRetailerCheckout = async (
  req: {
    retailerId: number;
    retailerIds: number[];
    checkoutBatchId: string;
    checkoutMode: string;
    currentStamp?: number;
    connectionInfo?: string;
  },
  signal: AbortSignal
) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckout/initiateRetailerCheckout`,
    req,
    {
      headers: await getHeaders(),
      signal,
    }
  );
  return data as {
    userRetailerCheckoutBatch: UserRetailerCheckoutBatch;
    userRetailerCheckoutOrder: UserRetailerCheckoutOrder;
    status: string;
    errorMessage?: string;
  };
};

export const retryRetailerLoginDuringCheckout = async (req: {
  orderId: string;
  currentStamp?: number;
  connectionInfo?: string;
}) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckout/retryLogin`,
    req,
    {
      headers: await getHeaders(),
    }
  );
  return data as {
    userRetailerCheckoutSession: UserRetailerLiveShoppingSession;
    status: string;
    errorMessage?: string;
  };
};

export const confirmCheckout = async (req: {
  retailerId: number;
  orderId: string;
  paymentConfirmation?: {
    type: "cvv" | "cardNumber" | "expirationDate" | "cardHolderName";
    value: string;
  };
}) => {
  try {
    const { data } = await axiosRetailerSync.post(
      `/retailerCheckout/updatePaymentConfirmationAndCompleteCheckout`,
      req,
      {
        headers: await getHeaders(),
      }
    );
    return data as {
      userRetailerCheckoutSession?: UserRetailerCheckoutEvent;
      status: string;
      errorMessage?: string;
    };
  } catch {
    throw new Error("Error confirming order");
  }
};

export const cancelCheckout = async (req: {
  retailerId: number;
  orderId: string;
}) => {
  try {
    const { data } = await axiosRetailerSync.post(
      `/retailerCheckout/cancelCheckout`,
      req,
      {
        headers: await getHeaders(),
      }
    );
    return data as {
      userRetailerCheckoutSession?: UserRetailerCheckoutEvent;
      status: string;
      errorMessage?: string;
    };
  } catch {
    throw new Error("Error cancelling order");
  }
};

export interface FailedAttemptType {
  type:
    | "login"
    | "updateShippingAddress"
    | "updatePaymentMethod"
    | "updateShippingOption"
    | "placeOrder"
    | "addShippingAddress"
    | "addPaymentMethod";
  errorMessage?: string;
  timestamp: number;
}

export interface UserRetailerLiveShoppingSession {
  userId: string;
  sessionId: string;
  syncId: string;
  retailerId: number;
  brandId: number;
  checkoutId: string;
  checkoutBatchId: string;
  checkoutMode: "clientSide" | "serverSide";
  connectStatusEvents?: Set<string>;
  checkoutStatusEvents?: Set<string>;
  eventsSequence?: UserRetailerCheckoutEvent[];
  errorMessage?: string;
  notificationEvents?: Set<string>;
  items?: ShoppingCartItem[];
  failureAttempts?: FailedAttemptType[];
  status: UserRetailerCheckoutSessionStatus;
  checkoutStatus: UserRetailerCheckoutSessionStatus;
  serverUpdatedAt: number;
  clientUpdatedAt: number;
  cartUpdatedTime: number;
  cartUpdateAt: number;
  createdTime: number;
  updatedTime: number;
  lastKnownActiveTime?: number;
}

export type EnterAnimationStaging =
  | "itemSelection"
  | "userAuthentication"
  | "orderSummary"
  | "success";

export interface RetailerCheckoutOrderStatus {
  userRetailerCheckoutOrder?: UserRetailerCheckoutOrder;
  userRetailerCheckoutSessionStatus?: {
    retailerId: number;
    status: UserRetailerCheckoutSessionStatus;
    createdAt: number;
    updatedAt: number;
    eventSequence: UserRetailerCheckoutEvent;
    lastFailedAttempt?: FailedAttemptType;
  };
  retailerSyncSessions?: RetailerSyncSession[];
  userRetailerSyncSessionStatus?: {
    createdAt: number;
    retailerId: number;
    syncId: string;
    syncStatus: SyncStatus;
    updatedAt: number;
  };
  displayStatusInfo?: {
    title: string;
    description: string;
    stage: EnterAnimationStaging;
  };
  status: string;
  products?: IProducts;
  errorMessage?: string;
}

export enum CheckoutOrderShipmentStatus {
  DELIVERED = "DELIVERED",
  ON_THE_WAY = "ON_THE_WAY",
  CANCELLED = "CANCELLED",
  FAILED = "FAILED",
}

export interface RetailerCheckoutOrderStatusV2 {
  userRetailerCheckoutOrder?: UserRetailerCheckoutOrder;
  retailerSyncSessions?: RetailerSyncSession[];
  displayStatusInfo?: {
    description: string;
    stage: CheckoutSectionDisplayStage;
    shipmentStatus?: CheckoutOrderShipmentStatus;
    displayErrorMessage?: string;
  };
  showConnectPage?: boolean;
  status: string;
  products?: IProducts;
}

export const getUserRetailerCheckoutBatch = async ({
  checkoutBatchId,
}: {
  checkoutBatchId: string;
}) => {
  const { data } = await axiosRetailerSync.get(
    `/retailerCheckout/getUserRetailerCheckoutBatch?checkoutBatchId=${checkoutBatchId}`,
    {
      headers: await getHeaders(),
    }
  );
  return data as UserRetailerCheckoutBatchStatus;
};

interface UpdateShippingToUserRetailerCheckoutOrderRequest {
  retailerId: number;
  orderId: string;
  shippingAddress: UserRetailerCheckoutOrder["shippingAddress"];
}
export const addShippingToUserRetailerCheckoutOrder = async (
  addShippingToUserRetailerCheckoutOrderRequest: UpdateShippingToUserRetailerCheckoutOrderRequest
) => {
  try {
    const { data } = await axiosRetailerSync.post(
      `/retailerCheckout/addShippingAddress`,
      addShippingToUserRetailerCheckoutOrderRequest,
      {
        headers: await getHeaders(),
      }
    );
    return data as {
      userRetailerCheckoutSession?: UserRetailerCheckoutEvent;
      status: string;
      errorMessage?: string;
    };
  } catch {
    throw new Error("Error adding shipping address");
  }
};

export const updateShippingToUserRetailerCheckoutOrder = async (
  updateShippingToUserRetailerCheckoutOrderRequest: UpdateShippingToUserRetailerCheckoutOrderRequest
) => {
  try {
    const { data } = await axiosRetailerSync.post(
      `/retailerCheckout/updateShippingAddress`,
      updateShippingToUserRetailerCheckoutOrderRequest,
      {
        headers: await getHeaders(),
      }
    );
    return data as {
      userRetailerCheckoutSession?: UserRetailerCheckoutEvent;
      status: string;
      errorMessage?: string;
    };
  } catch {
    throw new Error("Error updating shipping address");
  }
};

export const updateShippingOption = async (updateShippingOptionRequest: {
  retailerId: number;
  orderId: string;
  deliveryOption?: {
    name: string;
    cssSelector: string;
  };
  deliveryTimeWindow?: {
    name: string;
    cssSelector: string;
  };
}) => {
  try {
    const { data } = await axiosRetailerSync.post(
      `/retailerCheckout/updateShippingOption`,
      updateShippingOptionRequest,
      {
        headers: await getHeaders(),
      }
    );
    return data as {
      userRetailerCheckoutSession?: UserRetailerCheckoutEvent;
      status: string;
      errorMessage?: string;
    };
  } catch {
    throw new Error("Error updating shipping option");
  }
};

interface UpdatePaymentMethodToUserRetailerCheckoutOrderRequest {
  retailerId: number;
  orderId: string;
  cardInfo: {
    cardNumber?: string;
    cardIssuer: string;
    expirationDate: string;
    cvv?: string;
    cardHolderName: string;
    cardNumberEnding?: string;
  };
  billingAddress: UserRetailerCheckoutOrder["billingAddress"];
}
export const updatePaymentMethodToUserRetailerCheckoutOrder = async (
  updatePaymentMethodToUserRetailerCheckoutOrderRequest: UpdatePaymentMethodToUserRetailerCheckoutOrderRequest
) => {
  try {
    const { data } = await axiosRetailerSync.post(
      `/retailerCheckout/updatePaymentMethod`,
      updatePaymentMethodToUserRetailerCheckoutOrderRequest,
      {
        headers: await getHeaders(),
      }
    );
    return data as {
      userRetailerCheckoutSession?: UserRetailerCheckoutEvent;
      status: string;
      errorMessage?: string;
    };
  } catch {
    throw new Error("Error updating payment method");
  }
};

export const addPaymentMethodToUserRetailerCheckoutOrder = async (
  addPaymentMethodToUserRetailerCheckoutOrderRequest: UpdatePaymentMethodToUserRetailerCheckoutOrderRequest
) => {
  try {
    const { data } = await axiosRetailerSync.post(
      `/retailerCheckout/addPaymentMethod`,
      addPaymentMethodToUserRetailerCheckoutOrderRequest,
      {
        headers: await getHeaders(),
      }
    );
    return data as {
      userRetailerCheckoutSession?: UserRetailerCheckoutEvent;
      status: string;
      errorMessage?: string;
    };
  } catch {
    throw new Error("Error adding payment method");
  }
};

export const deleteItemsInCheckoutPage = async (req: {
  retailerId?: number;
  orderId?: string;
  brandId?: number;
  items?: {
    quantity?: number;
    retailPrice?: number;
    retailerSku?: string;
    retailerId?: number;
  }[];
}) => {
  try {
    const { data } = await axiosRetailerSync.post(
      `/retailerCheckout/deleteItemsInCheckoutPage`,
      req,
      {
        headers: await getHeaders(),
      }
    );
    return data as {
      userRetailerCheckoutSession?: UserRetailerCheckoutEvent;
      status: string;
      errorMessage?: string;
    };
  } catch {
    throw new Error("Error deleting items");
  }
};

export const updateUserConfirmationToCheckoutItems = async (req: {
  retailerId?: number;
  orderId?: string;
  brandId?: number;
  items?: {
    quantity?: number;
    retailPrice?: number;
    retailerSku?: string;
    retailerId?: number;
  }[];
}) => {
  try {
    const { data } = await axiosRetailerSync.post(
      `/retailerCheckout/updateUserConfirmationToCheckoutItems`,
      req,
      {
        headers: await getHeaders(),
      }
    );
    return data as {
      userRetailerCheckoutSession?: UserRetailerCheckoutEvent;
      status: string;
      errorMessage?: string;
    };
  } catch (e: unknown) {
    throw new Error("Error confirming order");
  }
};

export const deleteOutOfStockItemsFromCheckoutOrder = async (req: {
  retailerId?: number;
  orderId?: string;
  brandId?: number;
  items?: {
    quantity?: number;
    retailPrice?: number;
    retailerSku?: string;
    retailerId?: number;
  }[];
}) => {
  try {
    const { data } = await axiosRetailerSync.post(
      `/retailerCheckout/deleteOutOfStockItemsFromCheckoutOrder`,
      req,
      {
        headers: await getHeaders(),
      }
    );
    return data as {
      userRetailerCheckoutSession?: UserRetailerCheckoutEvent;
      status: string;
      errorMessage?: string;
    };
  } catch (e: unknown) {
    console.error("updateUserConfirmationToCheckoutItems error", e);
    throw new Error("Error deleting out of stock items");
  }
};

export const saveRetailerCredentials = async ({
  retailerId,
  checkoutIntentId,
  credentials,
}: {
  retailerId: string;
  checkoutIntentId: string;
  credentials: {
    username: string;
    password: string;
  };
}): Promise<CheckoutIntent> => {
  try {
    const { currentStamp, token } = (await getTOtp()) ?? {};

    if (!token) {
      throw new Error("No token found");
    }

    const connectionInfo = encryptData(
      {
        retailerId,
        credential: {
          username: credentials.username,
          password: credentials.password,
        },
      },
      token
    );

    const { data } = await axiosRetailerSync.post(
      `/retailerCheckoutV2/updateRetailerConnection`,
      {
        connectionInfo,
        currentStamp,
        checkoutIntentId,
      },
      {
        headers: await getHeaders(),
      }
    );
    return data.checkoutIntent as CheckoutIntent;
  } catch (error) {
    console.error("Error saving retailer credentials:", error);
    throw error;
  }
};

export interface CreateTestCheckoutResponse {
  status: "success" | "error";
  userRetailerCheckoutOrder?: UserRetailerCheckoutOrder;
  errorMessage?: string;
}

export const createAsyncCheckout = async ({
  isTestOrder = true,
  checkoutIntentId,
}: {
  isTestOrder?: boolean;
  checkoutIntentId: string;
  // TODO: replace all of this with the checkoutIntent ID
  // all below should be stored
  // Invariant: checkout button ALWAYS creates a new checkoutIntent
  // Invariant: "you are buying what you are seeing"
}): Promise<CreateTestCheckoutResponse> => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/submitOrder`,
    {
      isTestOrder,
      checkoutIntentId,
    },
    { headers: await getHeaders() }
  );
  return data;
};

export const createGuestCheckout = async ({
  checkoutIntentId,
}: {
  checkoutIntentId: string;
}) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/submitGuestOrder`,
    { checkoutIntentId },
    { headers: await getHeaders() }
  );
  return data;
};

export const reSubmitCheckout = async ({
  orderId,
}: {
  orderId: string;
}): Promise<CreateTestCheckoutResponse> => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/reSubmitOrder`,
    {
      orderId,
    },
    { headers: await getHeaders() }
  );
  return data;
};

export const submitUserInteraction = async (
  orderId: string,
  communicationId: string,
  req: {
    response: UserRetailerCheckoutCommunicationResponse;
  }
) => {
  const { data } = await axiosRetailerSync.post(
    `/checkout/communications/${orderId}/${communicationId}/respond`,
    req.response,
    { headers: await getHeaders() }
  );
  return data;
};

export const submitUserInteractionMock = async (
  orderId: string,
  communicationId: string,
  req: {
    response: UserRetailerCheckoutCommunicationResponse;
  }
) => {
  const { data } = await axiosRetailerSync.post(
    `/mockCheckout/${orderId}/${communicationId}/respond`,
    req,
    { headers: await getHeaders() }
  );
  return data;
};

export const getRetailerCheckoutOrderStatus = async (
  { orderId }: { orderId: string },
  abortSignal?: AbortSignal
) => {
  const { data } = await axiosRetailerSync.get(
    `/retailerCheckout/getRetailerCheckoutOrderStatus?orderId=${orderId}`,
    {
      headers: await getHeaders(),
      signal: abortSignal,
    }
  );

  return data as RetailerCheckoutOrderStatus;
};

export const getRetailerCheckoutOrderStatusV2 = async (
  { orderId }: { orderId: string },
  abortSignal?: AbortSignal
) => {
  const { data } = await axiosRetailerSync.get(
    `/retailerCheckoutV2/getRetailerCheckoutOrderStatus?orderId=${orderId}`,
    {
      headers: await getHeaders(),
      signal: abortSignal,
    }
  );

  return data as RetailerCheckoutOrderStatusV2;
};

export const getRetailerCheckoutOrderStatusV2Mock = async (
  { orderId }: { orderId: string },
  abortSignal?: AbortSignal
) => {
  const { data } = await axiosRetailerSync.get(
    `/mockCheckout/getRetailerCheckoutOrderStatusV2?orderId=${orderId}&retailerId=1&section=${CheckoutSection.VALIDATING_SHIPPING_ADDRESS}&interactionType=${UserInteractionType.ADDRESS_SELECTION}&status=${UserRetailerCheckoutOrderStatusV2.checkout_success}`,
    {
      headers: await getHeaders(),
      signal: abortSignal,
    }
  );
  return data as RetailerCheckoutOrderStatusV2;
};

export const updatePaymentMethod = async (req: {
  checkoutIntentId: string;
  paymentMethodId: string;
  shippingAddress?: CheckoutIntent["shippingAddress"];
  billingAddress?: CheckoutIntent["billingAddress"];
}) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/updatePaymentMethod`,
    req,
    { headers: await getHeaders() }
  );
  return data as {
    status: "success" | "error";
    errorMessage?: string;
    checkoutIntent?: CheckoutIntent;
  };
};

export const updateCheckoutItems = async (req: {
  checkoutIntentId: string;
  checkoutItems: {
    stacklineSku: string;
    retailerId: number;
    retailerSku: string;
    quantity: number;
  }[];
}) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/updatePurchaseItems`,
    req,
    { headers: await getHeaders() }
  );

  return data;
};

export const clearCheckoutPaymentIntent = async (req: {
  checkoutIntentId: string;
}) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/clearPaymentMethod`,
    req,
    { headers: await getHeaders() }
  );
  return data as {
    status: "success" | "error";
    errorMessage?: string;
    checkoutIntent?: CheckoutIntent;
  };
};

export const getCheckoutIntent = async ({
  checkoutIntentId,
  retailerId,
  checkoutItems,
  orderType,
}: {
  checkoutIntentId?: string | null;
  retailerId?: number;
  checkoutItems?: {
    retailerId: number;
    retailerSku: string;
    stacklineSku: string;
    quantity: number;
  }[];
  orderType?: AsyncCheckoutOrderType;
}) => {
  if (
    !checkoutIntentId &&
    (!checkoutItems || checkoutItems.length === 0 || !retailerId)
  ) {
    throw new Error(
      "Either checkoutIntentId or checkoutItems must be provided"
    );
  }
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/getCheckoutIntent`,
    { checkoutIntentId, retailerId, checkoutItems, orderType },
    { headers: await getHeaders() }
  );
  return data as CheckoutIntent;
};

export const updateOrderType = async (req: {
  checkoutIntentId: string;
  orderType: AsyncCheckoutOrderType;
}) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/updateOrderType`,
    req,
    { headers: await getHeaders() }
  );
  return data as {
    status: "success" | "error";
    errorMessage?: string;
    checkoutIntent?: CheckoutIntent;
  };
};

export const getShareableShoppingCart = async (
  {
    shoppingCartId,
  }: {
    shoppingCartId: string;
  },
  signal?: AbortSignal
) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/getShareableShoppingCart`,
    { shoppingCartId },
    { headers: await getHeaders(), signal }
  );
  return data as {
    items: ShoppingCartItem[];
  };
};

export const submitReturnRequest = async (req: {
  orderId: string;
  email?: string;
  items: {
    stacklineSku: string;
    retailerSku: string;
    returnQuantity: number;
    returnReason: string;
    returnReasonDescription: string;
  }[];
}) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckout/returns/create`,
    req,
    { headers: await getHeaders() }
  );
  return data;
};

export const switchToGuest = async ({ orderId }: { orderId: string }) => {
  const { data } = await axiosRetailerSync.post(
    `/retailerCheckoutV2/switchOrderToGuest`,
    { orderId, isTestOrder: false },
    { headers: await getHeaders() }
  );
  return data;
};
